import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Users, FolderKanban, FileVideo, Sparkles, ChevronDown, ChevronRight, RefreshCw, RotateCcw, Scissors, Mic, Layers, Activity, Coins, DollarSign, TrendingUp, Gift, Video, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ReadersAdminPanel } from '@/components/admin/ReadersAdminPanel';
import { HomepageHeroPanel } from '@/components/admin/HomepageHeroPanel';
import { PromoPopupsPanel } from '@/components/admin/PromoPopupsPanel';
import { StoreClipsPanel } from '@/components/admin/StoreClipsPanel';
import { MarketplaceOrderPanel } from '@/components/admin/MarketplaceOrderPanel';
import { MoneyOwedPanel } from '@/components/admin/MoneyOwedPanel';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface Project {
  _id: string;
  name: string;
  fileCount: number;
  clipCount: number;
  renderCount: number;
  createdAt: string;
  updatedAt: string;
}

interface PendingReview {
  _id: string;
  title: string;
  description?: string;
  price: number;
  productType: string;
  eventDate?: string;
  durationMins?: number | null;
  creatorEmail?: string;
  creatorName?: string;
  createdAt?: string;
}

interface PricingProduct {
  _id: string;
  title: string;
  productType: string;
  price: number;
  memberPriceCents: number | null;
  creatorEmail: string;
  published: boolean;
  eventDate: string | null;
  hasTicketTiers: boolean;
  ticketTiers?: { key: string; label: string; priceCents: number; memberPriceCents: number | null }[];
}

interface AdminProduct {
  id: string;
  title: string;
  type: string;
  creator: string;
  published: boolean;
  marketplaceListed: boolean;
  provider: string | null;
  priceCents: number;
  eventDate: string | null;
  createdAt: string | null;
  editUrl: string;
}

interface User {
  email: string;
  userId: string;
  projectCount: number;
  totalFiles: number;
  totalRenders: number;
  lastActivity: string | null;
  projects: Project[];
}

interface Stats {
  totalProjects: number;
  totalUsers: number;
  totalFiles: number;
  totalRenders: number;
  projectsToday: number;
  projectsThisWeek: number;
  projectsThisMonth: number;
}

interface ActivityItem {
  _id: string;
  tool: string;
  userEmail: string;
  timestamp: string;
  fileName: string | null;
  fileSize: number | null;
  success: boolean;
  error: string | null;
}

interface ToolStats {
  [key: string]: {
    total: number;
    success: number;
    failed: number;
  };
}

interface ActivityData {
  activities: ActivityItem[];
  toolStats: ToolStats;
  summary: {
    today: number;
    thisWeek: number;
    thisMonth: number;
    totalActivities: number;
    uniqueUsers: number;
  };
}

interface CreditStats {
  totalCreditsUsed: number;
  totalCreditsPurchased: number;
  totalRevenue: number;
  activeUsers: number;
  usageByOperation: {
    [key: string]: number;
  };
  topUsers: {
    userId: string;
    userEmail: string;
    creditsUsed: number;
    creditsPurchased: number;
  }[];
  recentTransactions: {
    userId: string;
    userEmail: string;
    type: string;
    amount: number;
    operationType: string;
    timestamp: string;
  }[];
}

interface CreditCost {
  operationType: string;
  cost: number;
  description: string;
}

interface MuxUsageProduct {
  productId: string;
  title: string;
  creatorEmail: string | null;
  assetCount: number;
  ingestMinutes: number;
  deliveryMinutes: number;
  statuses: Record<string, number>;
  overThreshold: boolean;
}

interface MuxUsage {
  configured: boolean;
  window: { days: number; start: string; end: string };
  alertMinutes: number;
  totals: {
    productCount: number;
    assetCount: number;
    ingestMinutes: number;
    deliveryMinutes: number;
    unmappedDeliveryMinutes: number;
  };
  alerts: MuxUsageProduct[];
  products: MuxUsageProduct[];
  deliveryError: string | null;
}

interface Subscription {
  userId: string;
  userEmail: string;
  status: string;
  tier: string;
  tierName: string;
  source: string;
  currentPeriodStart: string;
  currentPeriodEnd: string | null;
  credits: number;
  totalCredits: number;
  grantedBy?: string;
  grantedAt?: string;
}

const TOOL_ICONS: Record<string, React.ReactNode> = {
  rotator: <RotateCcw className="w-4 h-4" />,
  compressor: <FileVideo className="w-4 h-4" />,
  splitter: <Scissors className="w-4 h-4" />,
  stitcher: <Layers className="w-4 h-4" />,
  transcriber: <Mic className="w-4 h-4" />,
  enhancer: <Sparkles className="w-4 h-4" />
};

const TOOL_NAMES: Record<string, string> = {
  rotator: 'Video Rotator',
  compressor: 'Video Compressor',
  splitter: 'Video Splitter',
  stitcher: 'Video Stitcher',
  transcriber: 'Transcription',
  enhancer: 'Video Enhancer',
  concatenate: 'Merge',
  credit_usage_background_removal: 'BG Removal',
  credit_usage_shotstack_render: 'Render',
  credit_usage_audio_enhancement: 'Audio Enhance',
  credit_usage_video_generation: 'Video Gen',
  credit_usage_image_generation: 'Image Gen',
};

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [activityData, setActivityData] = useState<ActivityData | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Credit admin state
  const [creditStats, setCreditStats] = useState<CreditStats | null>(null);
  const [creditCosts, setCreditCosts] = useState<CreditCost[]>([]);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantAmount, setGrantAmount] = useState('10');
  const [grantType, setGrantType] = useState<'monthly' | 'purchased'>('monthly');
  const [isGranting, setIsGranting] = useState(false);

  // Subscription admin state
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [subGrantEmail, setSubGrantEmail] = useState('');
  const [subGrantTier, setSubGrantTier] = useState<'monthly' | 'creator' | 'cd_plus'>('monthly');
  const [subGrantDuration, setSubGrantDuration] = useState('12');
  const [subGrantCredits, setSubGrantCredits] = useState('');
  const [isGrantingSub, setIsGrantingSub] = useState(false);

  // Mux marketplace usage (Phase 3 cost monitoring)
  const [muxUsage, setMuxUsage] = useState<MuxUsage | null>(null);
  const [muxNotConfigured, setMuxNotConfigured] = useState(false);
  const [isLoadingMux, setIsLoadingMux] = useState(false);

  // First live-event review queue
  const [pendingReviews, setPendingReviews] = useState<PendingReview[]>([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [reviewActionId, setReviewActionId] = useState<string | null>(null);

  // Member pricing panel (admin-only knob — creators never see these fields)
  const [pricingQuery, setPricingQuery] = useState('');
  const [pricingResults, setPricingResults] = useState<PricingProduct[]>([]);
  const [isSearchingPricing, setIsSearchingPricing] = useState(false);
  const [pricingSavingId, setPricingSavingId] = useState<string | null>(null);
  const [memberPriceDrafts, setMemberPriceDrafts] = useState<Record<string, string>>({});

  // Products tab — every course/class/webinar on the platform + curation toggle.
  const [adminProducts, setAdminProducts] = useState<AdminProduct[]>([]);
  const [adminProductsLoading, setAdminProductsLoading] = useState(false);
  const [adminProductQuery, setAdminProductQuery] = useState('');
  const [listingBusyId, setListingBusyId] = useState<string | null>(null);

  const loadAdminProducts = async (q = '') => {
    setAdminProductsLoading(true);
    try {
      const res = await fetch(`/api/admin/products${q ? `?search=${encodeURIComponent(q)}` : ''}`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setAdminProducts(d.products || []); }
    } catch { /* ignore */ }
    setAdminProductsLoading(false);
  };

  const toggleListing = async (id: string, listed: boolean) => {
    setListingBusyId(id);
    try {
      const res = await fetch(`/api/admin/products/${id}/marketplace-listing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ listed }),
      });
      if (res.ok) setAdminProducts(prev => prev.map(p => p.id === id ? { ...p, marketplaceListed: listed } : p));
    } catch { /* ignore */ }
    setListingBusyId(null);
  };

  const searchPricingProducts = async (q: string) => {
    setIsSearchingPricing(true);
    try {
      const res = await fetch(`/api/admin/products/search?q=${encodeURIComponent(q)}`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setPricingResults(d.products || []); }
    } catch { /* non-fatal */ } finally { setIsSearchingPricing(false); }
  };

  // Per-tier member price on a tiered product (what checkout actually reads there).
  const [tierDrafts, setTierDrafts] = useState<Record<string, string>>({});
  const saveTierMemberPrice = async (id: string, tierKey: string, cents: number | null) => {
    setPricingSavingId(id);
    try {
      const res = await fetch(`/api/admin/products/${id}/pricing`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tierMemberPrices: { [tierKey]: cents } }),
      });
      if (res.ok) {
        setPricingResults(prev => prev.map(p => p._id === id ? { ...p, ticketTiers: (p.ticketTiers || []).map(t => t.key === tierKey ? { ...t, memberPriceCents: cents } : t) } : p));
        setTierDrafts(prev => ({ ...prev, [`${id}:${tierKey}`]: '' }));
      }
    } catch { /* non-fatal */ } finally { setPricingSavingId(null); }
  };

  const saveMemberPricing = async (id: string, cents: number | null) => {
    setPricingSavingId(id);
    try {
      const res = await fetch(`/api/admin/products/${id}/pricing`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memberPriceCents: cents }),
      });
      if (res.ok) {
        setPricingResults(prev => prev.map(p => p._id === id ? { ...p, memberPriceCents: cents } : p));
        setMemberPriceDrafts(prev => ({ ...prev, [id]: '' }));
      }
    } catch { /* non-fatal */ } finally { setPricingSavingId(null); }
  };

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const loadPendingReviews = async () => {
    setIsLoadingReviews(true);
    try {
      const res = await fetch('/api/admin/live-events/pending', { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setPendingReviews(d.pending || []); }
    } catch { /* non-fatal */ } finally { setIsLoadingReviews(false); }
  };

  const reviewLiveEvent = async (id: string, action: 'approve' | 'reject') => {
    let reason = '';
    if (action === 'reject') reason = window.prompt('Reason for rejection (emailed to the creator, optional):') || '';
    setReviewActionId(id);
    try {
      const res = await fetch(`/api/admin/live-events/${id}/${action}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(action === 'reject' ? { reason } : {}),
      });
      if (res.ok) setPendingReviews(prev => prev.filter(p => p._id !== id));
    } catch { /* non-fatal */ } finally { setReviewActionId(null); }
  };

  const checkAdminAccess = async () => {
    try {
      const response = await fetch('/api/admin/check', {
        credentials: 'include'
      });
      const data = await response.json();

      if (!data.isAdmin) {
        navigate('/');
        return;
      }

      setIsAdmin(true);
      await loadData();
      await loadCreditData();
      await loadSubscriptions();
      await loadMuxUsage();
    } catch (error) {
      console.error('Admin check failed:', error);
      navigate('/');
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [usersResponse, statsResponse, activityResponse] = await Promise.all([
        fetch('/api/admin/users', { credentials: 'include' }),
        fetch('/api/admin/stats', { credentials: 'include' }),
        fetch('/api/admin/activity?limit=50', { credentials: 'include' })
      ]);

      if (!usersResponse.ok || !statsResponse.ok) {
        throw new Error('Failed to load admin data');
      }

      const usersData = await usersResponse.json();
      const statsData = await statsResponse.json();
      const activityJson = activityResponse.ok ? await activityResponse.json() : null;

      setUsers(usersData.users || []);
      setStats(statsData);
      setActivityData(activityJson);
    } catch (error) {
      console.error('Failed to load data:', error);
      setError('Failed to load admin data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Load credit admin data
  const loadCreditData = async () => {
    try {
      const [statsResponse, packagesResponse] = await Promise.all([
        fetch('/api/admin/credits', { credentials: 'include' }),
        fetch('/api/credits/packages', { credentials: 'include' })
      ]);

      if (statsResponse.ok) {
        const data = await statsResponse.json();
        setCreditStats(data);
      }

      if (packagesResponse.ok) {
        const data = await packagesResponse.json();
        setCreditCosts(data.costs || []);
      }
    } catch (error) {
      console.error('Failed to load credit data:', error);
    }
  };

  // Load Mux marketplace usage (Phase 3). 503 means Mux isn't configured in this
  // environment — show a hint instead of an error.
  const loadMuxUsage = async () => {
    setIsLoadingMux(true);
    try {
      const response = await fetch('/api/admin/mux-usage', { credentials: 'include' });
      if (response.status === 503) {
        setMuxNotConfigured(true);
        setMuxUsage(null);
        return;
      }
      if (response.ok) {
        setMuxNotConfigured(false);
        setMuxUsage(await response.json());
      }
    } catch (error) {
      console.error('Failed to load Mux usage:', error);
    } finally {
      setIsLoadingMux(false);
    }
  };

  // Grant credits to a user
  const handleGrantCredits = async () => {
    if (!grantEmail || !grantAmount) return;

    setIsGranting(true);
    try {
      const response = await fetch('/api/admin/credits/grant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          userEmail: grantEmail,
          amount: parseInt(grantAmount),
          creditType: grantType
        })
      });

      if (!response.ok) {
        throw new Error('Failed to grant credits');
      }

      // Refresh credit data
      loadCreditData();
      setGrantEmail('');
      setGrantAmount('10');
    } catch (error) {
      console.error('Failed to grant credits:', error);
    } finally {
      setIsGranting(false);
    }
  };

  // Update credit cost
  const handleUpdateCost = async (operationType: string, newCost: number) => {
    try {
      const response = await fetch(`/api/admin/credits/costs/${operationType}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ cost: newCost })
      });

      if (response.ok) {
        loadCreditData();
      }
    } catch (error) {
      console.error('Failed to update cost:', error);
    }
  };

  // Load subscriptions
  const loadSubscriptions = async () => {
    try {
      const response = await fetch('/api/admin/subscriptions', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.subscriptions || []);
      }
    } catch (error) {
      console.error('Failed to load subscriptions:', error);
    }
  };

  // Grant subscription to user
  const handleGrantSubscription = async () => {
    if (!subGrantEmail) return;

    setIsGrantingSub(true);
    try {
      const response = await fetch('/api/admin/subscription/grant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          userEmail: subGrantEmail,
          tier: subGrantTier,
          durationMonths: subGrantDuration === 'lifetime' ? 'lifetime' : parseInt(subGrantDuration),
          credits: subGrantCredits ? parseInt(subGrantCredits) : 0
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to grant subscription');
      }

      // Refresh subscriptions
      await loadSubscriptions();
      setSubGrantEmail('');
      setSubGrantCredits('');
    } catch (error) {
      console.error('Failed to grant subscription:', error);
      alert(error instanceof Error ? error.message : 'Failed to grant subscription');
    } finally {
      setIsGrantingSub(false);
    }
  };

  // Revoke subscription
  const handleRevokeSubscription = async (userId: string) => {
    if (!confirm('Are you sure you want to revoke this subscription?')) return;

    try {
      const response = await fetch('/api/admin/subscription/revoke', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId })
      });

      if (response.ok) {
        await loadSubscriptions();
      }
    } catch (error) {
      console.error('Failed to revoke subscription:', error);
    }
  };

  const toggleUserExpanded = (email: string) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto px-4 py-6 max-w-7xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Admin Dashboard</h1>
            <p className="text-muted-foreground">View users, projects, and tool usage</p>
          </div>
          <Button
            variant="outline"
            onClick={loadData}
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {error && (
          <div className="bg-destructive/10 text-destructive rounded-lg p-4 mb-6">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Tabs defaultValue="overview" className="space-y-6" onValueChange={(v) => { if (v === 'reviews') loadPendingReviews(); if (v === 'products' && !adminProducts.length) loadAdminProducts(); }}>
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="activity">Tool Activity</TabsTrigger>
              <TabsTrigger value="users">Users & Projects</TabsTrigger>
              <TabsTrigger value="reviews">Reviews{pendingReviews.length ? ` (${pendingReviews.length})` : ''}</TabsTrigger>
              <TabsTrigger value="products">Products</TabsTrigger>
              <TabsTrigger value="pricing">Member pricing</TabsTrigger>
              <TabsTrigger value="readers">Readers</TabsTrigger>
              <TabsTrigger value="homepage">Homepage</TabsTrigger>
              <TabsTrigger value="popups">Pop-ups</TabsTrigger>
              <TabsTrigger value="credits">Video tokens</TabsTrigger>
              <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
              <TabsTrigger value="owed">Money owed</TabsTrigger>
              <TabsTrigger value="mux">Mux Usage</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {/* Stats Cards */}
              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Total Users
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold">{stats.totalUsers}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <FolderKanban className="w-4 h-4" />
                        Total Projects
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold">{stats.totalProjects}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <FileVideo className="w-4 h-4" />
                        Total Files
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold">{stats.totalFiles}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        Total Renders
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold">{stats.totalRenders}</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Tool Usage Stats */}
              {activityData?.toolStats && Object.keys(activityData.toolStats).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Activity className="w-5 h-5" />
                      Tool Usage
                    </CardTitle>
                    <CardDescription>Operations by tool type</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                      {Object.entries(activityData.toolStats).map(([tool, stats]) => (
                        <div key={tool} className="text-center p-3 rounded-lg bg-muted/50 overflow-hidden">
                          <div className="flex justify-center mb-2">
                            {TOOL_ICONS[tool] || <FileVideo className="w-4 h-4" />}
                          </div>
                          <p className="text-sm font-medium truncate" title={TOOL_NAMES[tool] || tool}>{TOOL_NAMES[tool] || tool}</p>
                          <p className="text-2xl font-bold">{stats.total}</p>
                          {stats.failed > 0 && (
                            <p className="text-xs text-destructive">{stats.failed} failed</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Activity Summary */}
              {activityData?.summary && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Activity Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Today:</span>{' '}
                        <span className="font-medium">{activityData.summary.today} operations</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">This Week:</span>{' '}
                        <span className="font-medium">{activityData.summary.thisWeek} operations</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">This Month:</span>{' '}
                        <span className="font-medium">{activityData.summary.thisMonth} operations</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Active Users:</span>{' '}
                        <span className="font-medium">{activityData.summary.uniqueUsers}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Activity Tab */}
            <TabsContent value="activity" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Activity</CardTitle>
                  <CardDescription>Last 50 operations across all tools</CardDescription>
                </CardHeader>
                <CardContent>
                  {activityData?.activities && activityData.activities.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tool</TableHead>
                          <TableHead>User</TableHead>
                          <TableHead>File</TableHead>
                          <TableHead>Size</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Time</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activityData.activities.map((activity) => (
                          <TableRow key={activity._id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {TOOL_ICONS[activity.tool] || <FileVideo className="w-4 h-4" />}
                                <span className="text-sm">{TOOL_NAMES[activity.tool] || activity.tool}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">
                              {activity.userEmail || 'Anonymous'}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                              {activity.fileName || '-'}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatFileSize(activity.fileSize)}
                            </TableCell>
                            <TableCell>
                              <Badge variant={activity.success ? 'default' : 'destructive'}>
                                {activity.success ? 'Success' : 'Failed'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatRelativeTime(activity.timestamp)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-muted-foreground text-center py-8">
                      No activity recorded yet. Activity will appear here as users use the tools.
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Users & Projects</CardTitle>
                  <CardDescription>Click on a user to expand their projects (Video Enhancer only)</CardDescription>
                </CardHeader>
                <CardContent>
                  {users.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No users found</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8"></TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead className="text-center">Projects</TableHead>
                          <TableHead className="text-center">Files</TableHead>
                          <TableHead className="text-center">Renders</TableHead>
                          <TableHead>Last Activity</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {users.map((user) => (
                          <>
                            <TableRow
                              key={user.email}
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => toggleUserExpanded(user.email)}
                            >
                              <TableCell>
                                {expandedUsers.has(user.email) ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                              </TableCell>
                              <TableCell className="font-medium">{user.email}</TableCell>
                              <TableCell className="text-center">
                                <Badge variant="secondary">{user.projectCount}</Badge>
                              </TableCell>
                              <TableCell className="text-center">{user.totalFiles}</TableCell>
                              <TableCell className="text-center">{user.totalRenders}</TableCell>
                              <TableCell className="text-muted-foreground">
                                {formatRelativeTime(user.lastActivity)}
                              </TableCell>
                            </TableRow>

                            {/* Expanded Projects */}
                            {expandedUsers.has(user.email) && user.projects.length > 0 && (
                              <TableRow>
                                <TableCell colSpan={6} className="bg-muted/30 p-0">
                                  <div className="px-8 py-3">
                                    <p className="text-sm font-medium text-muted-foreground mb-2">
                                      Projects by {user.email}
                                    </p>
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>Project Name</TableHead>
                                          <TableHead className="text-center">Files</TableHead>
                                          <TableHead className="text-center">Clips</TableHead>
                                          <TableHead className="text-center">Renders</TableHead>
                                          <TableHead>Created</TableHead>
                                          <TableHead>Updated</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {user.projects.map((project) => (
                                          <TableRow key={project._id}>
                                            <TableCell className="font-medium">
                                              {project.name || 'Unnamed Project'}
                                            </TableCell>
                                            <TableCell className="text-center">
                                              {project.fileCount}
                                            </TableCell>
                                            <TableCell className="text-center">
                                              {project.clipCount}
                                            </TableCell>
                                            <TableCell className="text-center">
                                              {project.renderCount}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground text-sm">
                                              {formatDate(project.createdAt)}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground text-sm">
                                              {formatRelativeTime(project.updatedAt)}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Reviews Tab — first live event (featured event / live class) per creator */}
            <TabsContent value="reviews" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <CardTitle>Live events awaiting review</CardTitle>
                      <CardDescription>A creator's first featured event / live class is held here before it goes live. Approve to publish it; the creator's later live events publish automatically.</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={loadPendingReviews} disabled={isLoadingReviews}>
                      {isLoadingReviews ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {isLoadingReviews ? (
                    <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
                  ) : pendingReviews.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">Nothing waiting for review. 🎉</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead>Creator</TableHead>
                          <TableHead>When</TableHead>
                          <TableHead>Price</TableHead>
                          <TableHead className="text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendingReviews.map(p => (
                          <TableRow key={p._id}>
                            <TableCell>
                              <div className="font-medium">{p.title || 'Untitled'}</div>
                              {p.description && <div className="text-xs text-muted-foreground line-clamp-2 max-w-md">{p.description}</div>}
                            </TableCell>
                            <TableCell className="text-sm">
                              <div>{p.creatorName || '—'}</div>
                              <div className="text-xs text-muted-foreground">{p.creatorEmail}</div>
                            </TableCell>
                            <TableCell className="text-sm">{p.eventDate ? new Date(p.eventDate).toLocaleString() : '—'}</TableCell>
                            <TableCell className="text-sm">{p.price === 0 ? 'Free' : `$${(p.price / 100).toFixed(2)}`}</TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              <Button size="sm" variant="outline" className="mr-2" disabled={reviewActionId === p._id} onClick={() => reviewLiveEvent(p._id, 'reject')}>Reject</Button>
                              <Button size="sm" disabled={reviewActionId === p._id} onClick={() => reviewLiveEvent(p._id, 'approve')}>
                                {reviewActionId === p._id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Approve'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Member Pricing Tab — the admin-only knob for "Free with Plus" /
                member prices. Creators can never set these on their own products. */}
            <TabsContent value="products" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>All courses &amp; classes</CardTitle>
                  <CardDescription>
                    Every product on the platform. <strong>Listed</strong> = shown in the public marketplace (curated).
                    Unlisted products are still live on the creator's own store &amp; link — they just don't appear in the marketplace grid.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form
                    className="flex gap-2 mb-4"
                    onSubmit={(e) => { e.preventDefault(); loadAdminProducts(adminProductQuery); }}
                  >
                    <Input
                      placeholder="Search by title…"
                      value={adminProductQuery}
                      onChange={(e) => setAdminProductQuery(e.target.value)}
                      className="max-w-md"
                    />
                    <Button type="submit" variant="outline" disabled={adminProductsLoading}>
                      {adminProductsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                    </Button>
                  </form>
                  {adminProducts.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">
                      {adminProductsLoading ? 'Loading…' : 'No products found.'}
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Product</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {adminProducts.map(p => (
                          <TableRow key={p.id}>
                            <TableCell>
                              <div className="font-medium">{p.title}</div>
                              <div className="text-xs text-muted-foreground">
                                {p.type}{p.eventDate ? ` · ${new Date(p.eventDate).toLocaleDateString()}` : ''} · {p.creator}
                                {p.provider ? ` · ${p.provider}` : ''}
                              </div>
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              <Badge variant="outline">{p.published ? 'published' : 'draft'}</Badge>{' '}
                              {p.marketplaceListed
                                ? <Badge>listed</Badge>
                                : <Badge variant="secondary">unlisted</Badge>}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              <a href={p.editUrl} className="text-sm text-primary underline mr-3">Edit</a>
                              <Button
                                size="sm"
                                variant={p.marketplaceListed ? 'outline' : 'default'}
                                disabled={listingBusyId === p.id || p.provider === 'ita'}
                                title={p.provider === 'ita' ? 'ITA partner feed — always in the marketplace' : ''}
                                onClick={() => toggleListing(p.id, !p.marketplaceListed)}
                              >
                                {listingBusyId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (p.marketplaceListed ? 'Unlist' : 'List')}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="readers" className="space-y-6">
              <ReadersAdminPanel />
            </TabsContent>

            <TabsContent value="homepage" className="space-y-6">
              <HomepageHeroPanel />
              <MarketplaceOrderPanel />
              <StoreClipsPanel />
            </TabsContent>

            <TabsContent value="owed" className="space-y-6">
              <MoneyOwedPanel />
            </TabsContent>

            <TabsContent value="popups" className="space-y-6">
              <PromoPopupsPanel />
            </TabsContent>

            <TabsContent value="pricing" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Member pricing</CardTitle>
                  <CardDescription>
                    Set what Hey Joe Plus members pay for a product — a specific price, or $0 for "Free with Plus".
                    No member price set = members pay full price. Admin-only: creators never see or set these fields.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form
                    className="flex gap-2 mb-4"
                    onSubmit={(e) => { e.preventDefault(); searchPricingProducts(pricingQuery); }}
                  >
                    <Input
                      placeholder="Search by product title or creator email…"
                      value={pricingQuery}
                      onChange={(e) => setPricingQuery(e.target.value)}
                      className="max-w-md"
                    />
                    <Button type="submit" variant="outline" disabled={isSearchingPricing}>
                      {isSearchingPricing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                    </Button>
                  </form>
                  {pricingResults.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">
                      {isSearchingPricing ? 'Searching…' : 'Search for a product to manage its member pricing.'}
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Product</TableHead>
                          <TableHead>Price</TableHead>
                          <TableHead>Member price</TableHead>
                          <TableHead className="text-right">Set member pricing</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pricingResults.map(p => (
                          <TableRow key={p._id}>
                            <TableCell>
                              <div className="font-medium">{p.title || 'Untitled'} {!p.published && <Badge variant="outline" className="ml-1">draft</Badge>}</div>
                              <div className="text-xs text-muted-foreground">
                                {p.productType}{p.eventDate ? ` · ${new Date(p.eventDate).toLocaleDateString()}` : ''} · {p.creatorEmail}
                              </div>
                              {p.hasTicketTiers && (
                                <div className="mt-2 space-y-1.5">
                                  <div className="text-xs text-muted-foreground">Ticket types. Member pricing is set per type here; the product-level value on the right is ignored at checkout.</div>
                                  {(p.ticketTiers || []).map(t => {
                                    const dk = `${p._id}:${t.key}`;
                                    return (
                                      <div key={t.key} className="flex items-center gap-2 flex-wrap text-xs">
                                        <span className="font-medium">{t.label}</span>
                                        <span className="text-muted-foreground">{t.priceCents === 0 ? 'Free' : `$${(t.priceCents / 100).toFixed(2)}`}</span>
                                        <span className="text-muted-foreground">·</span>
                                        <span>{t.memberPriceCents === null ? <span className="text-muted-foreground">members pay full</span> : t.memberPriceCents === 0 ? <Badge>Free with Plus</Badge> : `members $${(t.memberPriceCents / 100).toFixed(2)}`}</span>
                                        <Input type="number" min="0" step="0.01" placeholder="$" className="w-20 h-7 inline-block text-xs" value={tierDrafts[dk] ?? ''} onChange={(e) => setTierDrafts(prev => ({ ...prev, [dk]: e.target.value }))} />
                                        <Button size="sm" variant="outline" className="h-7" disabled={pricingSavingId === p._id || !(tierDrafts[dk] ?? '').trim()} onClick={() => { const c = Math.round(parseFloat(tierDrafts[dk] || '') * 100); if (Number.isFinite(c) && c >= 0) saveTierMemberPrice(p._id, t.key, c); }}>Set</Button>
                                        <Button size="sm" className="h-7" disabled={pricingSavingId === p._id} onClick={() => saveTierMemberPrice(p._id, t.key, 0)}>Free</Button>
                                        <Button size="sm" variant="ghost" className="h-7" disabled={pricingSavingId === p._id || t.memberPriceCents === null} onClick={() => saveTierMemberPrice(p._id, t.key, null)}>Clear</Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-sm whitespace-nowrap">{p.price === 0 ? 'Free' : `$${(p.price / 100).toFixed(2)}`}</TableCell>
                            <TableCell className="text-sm whitespace-nowrap">
                              {p.memberPriceCents === null
                                ? <span className="text-muted-foreground">Full price (none set)</span>
                                : p.memberPriceCents === 0
                                  ? <Badge>Free with Plus</Badge>
                                  : `$${(p.memberPriceCents / 100).toFixed(2)}`}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              <div className="inline-flex items-center gap-2">
                                <Input
                                  type="number" min="0" step="0.01" placeholder="$"
                                  className="w-24 inline-block"
                                  value={memberPriceDrafts[p._id] ?? ''}
                                  onChange={(e) => setMemberPriceDrafts(prev => ({ ...prev, [p._id]: e.target.value }))}
                                />
                                <Button
                                  size="sm" variant="outline"
                                  disabled={pricingSavingId === p._id || !(memberPriceDrafts[p._id] ?? '').trim()}
                                  onClick={() => {
                                    const cents = Math.round(parseFloat(memberPriceDrafts[p._id] || '') * 100);
                                    if (Number.isFinite(cents) && cents >= 0) saveMemberPricing(p._id, cents);
                                  }}
                                >
                                  Set
                                </Button>
                                <Button size="sm" disabled={pricingSavingId === p._id} onClick={() => saveMemberPricing(p._id, 0)}>
                                  {pricingSavingId === p._id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Free for members'}
                                </Button>
                                <Button size="sm" variant="ghost" disabled={pricingSavingId === p._id || p.memberPriceCents === null} onClick={() => saveMemberPricing(p._id, null)}>
                                  Clear
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Credits Tab */}
            <TabsContent value="credits" className="space-y-6">
              {/* Credit Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription className="flex items-center gap-2">
                      <Coins className="w-4 h-4" />
                      Video tokens used
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{creditStats?.totalCreditsUsed || 0}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Video tokens purchased
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{creditStats?.totalCreditsPurchased || 0}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Revenue
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">${((creditStats?.totalRevenue || 0) / 100).toFixed(2)}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Active video token users
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{creditStats?.activeUsers || 0}</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Grant Credits Form */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Gift className="w-5 h-5" />
                      Grant video tokens
                    </CardTitle>
                    <CardDescription>Manually grant video tokens to a user</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="grant-email">User Email</Label>
                      <Input
                        id="grant-email"
                        type="email"
                        placeholder="user@example.com"
                        value={grantEmail}
                        onChange={(e) => setGrantEmail(e.target.value)}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="grant-amount">Amount</Label>
                        <Input
                          id="grant-amount"
                          type="number"
                          min="1"
                          value={grantAmount}
                          onChange={(e) => setGrantAmount(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="grant-type">Video token type</Label>
                        <Select value={grantType} onValueChange={(v: 'monthly' | 'purchased') => setGrantType(v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="purchased">Purchased</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button
                      onClick={handleGrantCredits}
                      disabled={isGranting || !grantEmail || !grantAmount}
                      className="w-full"
                    >
                      {isGranting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Grant video tokens
                    </Button>
                  </CardContent>
                </Card>

                {/* Credit Costs */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Coins className="w-5 h-5" />
                      Video token costs
                    </CardTitle>
                    <CardDescription>Configure video token costs per operation</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Operation</TableHead>
                          <TableHead className="text-right">Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {creditCosts.map((cost) => (
                          <TableRow key={cost.operationType}>
                            <TableCell className="font-medium">{cost.description || cost.operationType}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Input
                                  type="number"
                                  min="0"
                                  className="w-16 text-right"
                                  defaultValue={cost.cost}
                                  onBlur={(e) => {
                                    const newCost = parseInt(e.target.value);
                                    if (newCost !== cost.cost) {
                                      handleUpdateCost(cost.operationType, newCost);
                                    }
                                  }}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>

              {/* Top Users by Credit Usage */}
              <Card>
                <CardHeader>
                  <CardTitle>Top video token users</CardTitle>
                  <CardDescription>Users with the highest video token activity</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead className="text-right">Video tokens used</TableHead>
                        <TableHead className="text-right">Video tokens purchased</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(creditStats?.topUsers || []).map((user) => (
                        <TableRow key={user.userId}>
                          <TableCell className="font-medium">{user.userEmail}</TableCell>
                          <TableCell className="text-right">{user.creditsUsed}</TableCell>
                          <TableCell className="text-right">{user.creditsPurchased}</TableCell>
                        </TableRow>
                      ))}
                      {(!creditStats?.topUsers || creditStats.topUsers.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-muted-foreground">
                            No video token activity yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Recent Transactions */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Transactions</CardTitle>
                  <CardDescription>Latest video token usage and purchases</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Operation</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Time</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(creditStats?.recentTransactions || []).map((tx, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{tx.userEmail}</TableCell>
                          <TableCell>
                            <Badge variant={tx.type === 'usage' ? 'secondary' : tx.type === 'purchase' ? 'default' : 'outline'}>
                              {tx.type}
                            </Badge>
                          </TableCell>
                          <TableCell>{tx.operationType || '-'}</TableCell>
                          <TableCell className="text-right">
                            <span className={tx.type === 'usage' ? 'text-red-500' : 'text-green-500'}>
                              {tx.type === 'usage' ? '-' : '+'}{tx.amount}
                            </span>
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {formatRelativeTime(tx.timestamp)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!creditStats?.recentTransactions || creditStats.recentTransactions.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">
                            No transactions yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Subscriptions Tab */}
            <TabsContent value="subscriptions" className="space-y-6">
              {/* Grant Subscription Form */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gift className="w-5 h-5" />
                    Grant Subscription
                  </CardTitle>
                  <CardDescription>Give a user a paid subscription (bypasses Stripe)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="sub-grant-email">User Email</Label>
                    <Input
                      id="sub-grant-email"
                      type="email"
                      placeholder="user@example.com"
                      value={subGrantEmail}
                      onChange={(e) => setSubGrantEmail(e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="sub-grant-tier">Tier</Label>
                      <Select value={subGrantTier} onValueChange={(v: 'monthly' | 'creator' | 'cd_plus') => setSubGrantTier(v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Hey Joe Plus ($10/mo)</SelectItem>
                          <SelectItem value="creator">Creator ($15/mo)</SelectItem>
                          <SelectItem value="cd_plus">CD Plus ($99/mo)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sub-grant-duration">Duration</Label>
                      <Select value={subGrantDuration} onValueChange={setSubGrantDuration}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 month</SelectItem>
                          <SelectItem value="3">3 months</SelectItem>
                          <SelectItem value="6">6 months</SelectItem>
                          <SelectItem value="12">12 months</SelectItem>
                          <SelectItem value="lifetime">Lifetime</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sub-grant-credits">Extra video tokens (optional)</Label>
                      <Input
                        id="sub-grant-credits"
                        type="number"
                        min="0"
                        placeholder="Use tier default"
                        value={subGrantCredits}
                        onChange={(e) => setSubGrantCredits(e.target.value)}
                      />
                    </div>
                  </div>
                  <Button
                    onClick={handleGrantSubscription}
                    disabled={isGrantingSub || !subGrantEmail}
                    className="w-full"
                  >
                    {isGrantingSub ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Grant Subscription
                  </Button>
                </CardContent>
              </Card>

              {/* Active Subscriptions List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Active Subscriptions
                  </CardTitle>
                  <CardDescription>All users with active subscriptions</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Tier</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead className="text-right">Video tokens</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subscriptions.map((sub) => (
                        <TableRow key={sub.userId}>
                          <TableCell className="font-medium">{sub.userEmail}</TableCell>
                          <TableCell>
                            <Badge variant={sub.tier === 'cd_plus' ? 'default' : (sub.tier === 'creator' || sub.tier === 'reader') ? 'secondary' : 'outline'}>
                              {sub.tierName || sub.tier}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {sub.source === 'admin_grant' ? `Admin (${sub.grantedBy || 'unknown'})` :
                             sub.source === 'ambassador' ? 'Ambassador' : 'Stripe'}
                          </TableCell>
                          <TableCell className="text-right">{sub.totalCredits}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {sub.currentPeriodEnd ? formatDate(sub.currentPeriodEnd) : 'Lifetime'}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleRevokeSubscription(sub.userId)}
                            >
                              Revoke
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {subscriptions.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground">
                            No active subscriptions
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Mux Usage Tab — Phase 3 cost monitoring */}
            <TabsContent value="mux" className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Video className="w-5 h-5" />
                    Mux Marketplace Usage
                  </h2>
                  {muxUsage && (
                    <p className="text-sm text-muted-foreground">
                      Last {muxUsage.window.days} days · delivery alert threshold{' '}
                      {muxUsage.alertMinutes.toLocaleString()} min/product
                    </p>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={loadMuxUsage} disabled={isLoadingMux}>
                  {isLoadingMux ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  <span className="ml-2">Refresh</span>
                </Button>
              </div>

              {muxNotConfigured && (
                <Card>
                  <CardContent className="py-8 text-center text-muted-foreground">
                    Mux isn't configured in this environment. Set the <code>MUX_*</code> env vars
                    and <code>MUX_MARKETPLACE_ENABLED</code> to enable usage reporting.
                  </CardContent>
                </Card>
              )}

              {muxUsage && (
                <>
                  {muxUsage.deliveryError && (
                    <Card className="border-destructive">
                      <CardContent className="py-3 text-sm text-destructive flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Delivery usage fetch failed: {muxUsage.deliveryError}. Ingest numbers below
                        are still accurate.
                      </CardContent>
                    </Card>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>Products with Mux assets</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold">{muxUsage.totals.productCount}</p>
                        <p className="text-xs text-muted-foreground">{muxUsage.totals.assetCount} assets</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>Ingest minutes</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold">{muxUsage.totals.ingestMinutes.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">encoded (one-time)</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>Delivery minutes</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold">{muxUsage.totals.deliveryMinutes.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">streamed in window</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>Unmapped delivery</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold">{muxUsage.totals.unmappedDeliveryMinutes.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">orphaned/deleted assets</p>
                      </CardContent>
                    </Card>
                  </div>

                  {muxUsage.alerts.length > 0 && (
                    <Card className="border-destructive">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2 text-destructive">
                          <AlertTriangle className="w-5 h-5" />
                          {muxUsage.alerts.length} product{muxUsage.alerts.length === 1 ? '' : 's'} over threshold
                        </CardTitle>
                        <CardDescription>
                          Delivery minutes ≥ {muxUsage.alertMinutes.toLocaleString()} in the last {muxUsage.window.days} days
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  )}

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Per-product usage</CardTitle>
                      <CardDescription>Sorted by delivery minutes</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {muxUsage.products.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No products have Mux assets yet.</p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Product</TableHead>
                              <TableHead>Creator</TableHead>
                              <TableHead className="text-right">Assets</TableHead>
                              <TableHead className="text-right">Ingest min</TableHead>
                              <TableHead className="text-right">Delivery min</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {muxUsage.products.map((p) => (
                              <TableRow key={p.productId}>
                                <TableCell className="font-medium">
                                  <span className="flex items-center gap-2">
                                    {p.overThreshold && <AlertTriangle className="w-4 h-4 text-destructive" />}
                                    {p.title}
                                  </span>
                                </TableCell>
                                <TableCell className="text-muted-foreground">{p.creatorEmail || '—'}</TableCell>
                                <TableCell className="text-right">{p.assetCount}</TableCell>
                                <TableCell className="text-right">{p.ingestMinutes.toLocaleString()}</TableCell>
                                <TableCell className="text-right">
                                  <span className={p.overThreshold ? 'text-destructive font-semibold' : ''}>
                                    {p.deliveryMinutes.toLocaleString()}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-wrap gap-1">
                                    {Object.entries(p.statuses).map(([status, count]) => (
                                      <Badge
                                        key={status}
                                        variant={status === 'ready' ? 'default' : status === 'errored' ? 'destructive' : 'secondary'}
                                      >
                                        {status} {count}
                                      </Badge>
                                    ))}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
};
